import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ben, oturumuGeriKur } from './api/kimlik';
import { oturumDusunceDinle } from './api/istemci';
import { sorguIstemcisiOlustur } from './api/sorgu-istemcisi';
import { oturumDeposuKullan } from './durum/oturum-deposu';
import { BirimAgaci } from './ekranlar/BirimAgaci';
import { Cihazlarim } from './ekranlar/Cihazlarim';
import { Giris } from './ekranlar/Giris';
import { IlkGiris } from './ekranlar/IlkGiris';
import { PersonelKarti } from './ekranlar/PersonelKarti';
import { PersonelListesi } from './ekranlar/PersonelListesi';
import { Kullanicilar } from './ekranlar/Kullanicilar';
import { MesaiOnIzni } from './ekranlar/MesaiOnIzni';
import { PuantajCizelgesi } from './ekranlar/PuantajCizelgesi';
import { PuantajDenetimi } from './ekranlar/PuantajDenetimi';
import { Ayarlar } from './ekranlar/Ayarlar';
import { Atamalar } from './ekranlar/Atamalar';
import { Donem } from './ekranlar/Donem';
import { Gorev } from './ekranlar/Gorev';
import { Izin } from './ekranlar/Izin';
import { Rehber } from './ekranlar/Rehber';
import { RehberMaddesi } from './ekranlar/RehberMaddesi';
import { VardiyaPlani } from './ekranlar/VardiyaPlani';
import { Kabuk } from './kabuk/Kabuk';

/**
 * UYGULAMA KÖKÜ.
 *
 * Üç işi vardır: sağlayıcıları kurmak, açılışta oturumu çerezle geri
 * kurmak, ve `asama`ya göre yönlendirmek.
 */

/**
 * SORGU İSTEMCİSİ BİLEŞEN DIŞINDA ÜRETİLİR.
 *
 * Gövde içinde üretilseydi her render'da yeni istemci oluşur, önbellek
 * sıfırlanır ve tüm sorgular yeniden koşardı.
 */
const sorguIstemcisi = sorguIstemcisiOlustur();

function OturumKurucu({ cocuk }: { cocuk: React.ReactNode }): React.ReactElement {
  const asama = oturumDeposuKullan((d) => d.asama);
  const kullaniciyiYaz = oturumDeposuKullan((d) => d.kullaniciyiYaz);
  const yetkileriYaz = oturumDeposuKullan((d) => d.yetkileriYaz);
  const dustu = oturumDeposuKullan((d) => d.dustu);
  const kuyruk = useQueryClient();

  useEffect(() => {
    // Oturum düşme olayı React ağacının dışından gelir (istek katmanı).
    return oturumDusunceDinle(() => {
      dustu();
    });
  }, [dustu]);

  useEffect(() => {
    if (asama !== 'anonim') return;

    /*
     * ÇIKIŞTA SORGU ÖNBELLEĞİ TEMİZLENİR.
     *
     * Temizlenmezse aynı tarayıcıda giriş yapan İKİNCİ kullanıcı, önceki
     * kullanıcının bildirimlerini ve oturum listesini önbellekten görür.
     * Veriler sunucudan yeniden çekilene kadar ekranda kalır ve bu bir
     * veri sızıntısıdır.
     */
    kuyruk.clear();
  }, [asama, kuyruk]);

  useEffect(() => {
    let iptalEdildi = false;

    /*
     * AÇILIŞTA SESSİZ YENİLEME.
     *
     * Erişim jetonu yalnız bellekte olduğu için sayfa yenilendiğinde
     * kaybolur; yenileme çerezi ise durur. Bu çağrı olmasaydı kullanıcı her
     * F5'te yeniden giriş yapardı. Başarısızlık NORMALDİR: hiç giriş
     * yapmamış kullanıcıda çerez yoktur.
     */
    oturumuGeriKur().then(
      (kullanici) => {
        if (!iptalEdildi) kullaniciyiYaz(kullanici);
      },
      () => {
        if (!iptalEdildi) dustu();
      },
    );

    return () => {
      iptalEdildi = true;
    };
  }, [kullaniciyiYaz, dustu]);

  useEffect(() => {
    /*
     * YETKİLER YALNIZ `acik` AŞAMADA ÇEKİLİR.
     *
     * `kisitli` aşamada `/kimlik/ben` 403 döner (şifre/aydınlatma kısıtı);
     * koşulsuz çağrılsaydı her ilk girişte bir başarısız istek ve gereksiz
     * bir hata kaydı oluşurdu.
     */
    if (asama !== 'acik') return;

    let iptalEdildi = false;

    ben().then(
      (bilgi) => {
        if (!iptalEdildi) yetkileriYaz(bilgi.yetkiler);
      },
      () => {
        // Yetkiler alınamazsa menü boş kalır; oturum düşürülmez.
        // İstek katmanı 401 durumunda zaten oturumu kendisi düşürür.
      },
    );

    return () => {
      iptalEdildi = true;
    };
  }, [asama, yetkileriYaz]);

  if (asama === 'kuruluyor') {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        Oturum kontrol ediliyor…
      </div>
    );
  }

  return <>{cocuk}</>;
}

function Yonlendirmeler(): React.ReactElement {
  const asama = oturumDeposuKullan((d) => d.asama);

  if (asama === 'anonim') {
    return (
      <Routes>
        <Route path="/giris" element={<Giris />} />
        <Route path="/*" element={<Navigate to="/giris" replace />} />
      </Routes>
    );
  }

  /*
   * KISITLI AŞAMADA BAŞKA HİÇBİR YOL AÇILMAZ.
   *
   * Sunucu zaten 403 döndürüyor; panelin de kapatması, kullanıcının her
   * isteği reddedilen boş bir arayüzde dolaşmasını engeller.
   */
  if (asama === 'kisitli') {
    return (
      <Routes>
        <Route path="/*" element={<IlkGiris />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/giris" element={<Navigate to="/" replace />} />
      <Route element={<Kabuk />}>
        <Route path="/birim-agaci" element={<BirimAgaci />} />
        <Route path="/personel" element={<PersonelListesi />} />
        <Route path="/personel/:personelId" element={<PersonelKarti />} />
        <Route path="/vardiya-plani" element={<VardiyaPlani />} />
        <Route path="/kullanicilar" element={<Kullanicilar />} />
        <Route path="/mesai-on-izni" element={<MesaiOnIzni />} />
        <Route path="/puantaj-denetimi" element={<PuantajDenetimi />} />
        {/*
          KİŞİ ÇİZELGESİ MENÜDE DEĞİL, DERİN BAĞLANTIYLA açılır: kişi
          seçilmeden anlamı yok. Menüye konsaydı boş bir "atama seç"
          ekranı gerekirdi; giriş noktaları denetim listesi ve personel kartı.
        */}
        <Route path="/puantaj/:atamaId" element={<PuantajCizelgesi />} />
        <Route path="/rehber" element={<Rehber />} />
        {/*
          MADDE AYRI ROTA, kip değil: adres PAYLAŞILABİLİR olmalı — bir
          itirazda "şu maddeye bakın" denebilmesi gerekiyor.
        */}
        <Route path="/rehber/madde/:kod" element={<RehberMaddesi />} />
        <Route path="/atamalar" element={<Atamalar />} />
        <Route path="/donem" element={<Donem />} />
        <Route path="/gorev" element={<Gorev />} />
        <Route path="/izin" element={<Izin />} />
        <Route path="/ayarlar" element={<Ayarlar />} />
        <Route path="/cihazlarim" element={<Cihazlarim />} />
        <Route path="/" element={<Navigate to="/birim-agaci" replace />} />
        <Route path="/*" element={<BulunamadiEkrani />} />
      </Route>
    </Routes>
  );
}

function BulunamadiEkrani(): React.ReactElement {
  return (
    <div className="mx-auto max-w-md py-12 text-center">
      <p className="text-sm font-medium text-slate-900">Sayfa bulunamadı</p>
      <p className="mt-1 text-sm text-slate-600">Bu ekran henüz eklenmedi ya da adres yanlış.</p>
    </div>
  );
}

export function Uygulama(): React.ReactElement {
  return (
    <QueryClientProvider client={sorguIstemcisi}>
      <BrowserRouter>
        <OturumKurucu cocuk={<Yonlendirmeler />} />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
